import React from 'react';
import { useParams } from 'react-router';
import { FormControl, Stack } from '@mui/material';
import PrimaryButton from '../../common/components/PrimaryButton';
import PasswordTextField from './PasswordTextField';
import useResetPasswordFormManager from '../hooks/useResetPasswordFormManager';
import Loading from '../../common/components/Loading';
import ConfirmDialog from '../../common/components/ConfirmDialog';
import config from '../../api/config';
import useValidateSecretManager from '../hooks/useValidateSecretManager';
import useResetPassword from '../hooks/useResetPassword';
import ResetPasswordCard from './ResetPasswordCard';

interface RouteParams {
  secret?: string;
}

export default function ResetPassword() {
  const { secret } = useParams<RouteParams>();

  const {
    isValidatingLink,
    isValidLink,
    hasServerError,
    setHasServerError
  } = useValidateSecretManager(secret);

  const {
      resettingInProcess,
      resettingComplete,
      reset,
      serverErrorMessages,
      wasResetPasswordUnsuccessful,
      setWasResetPasswordUnsuccessful
  } = useResetPassword(secret);
  
  const {
      password,
      setPassword,
      passwordValidated,
      passwordErrorMessage,
      confirmPassword,
      setConfirmPassword,
      confirmPasswordValidated,
      confirmPasswordErrorMessage,
      resetPasswordWithForm,
      showValidationErrors,
      isInProcess,
      resetFormSuccessfulOrNotSubmitted,
  } = useResetPasswordFormManager(
    reset,
    resettingInProcess,
    resettingComplete,
    serverErrorMessages
  );

  if (isValidatingLink) {
    return <Loading />;
  }

  return (
    <ResetPasswordCard>
      <Stack spacing={3}>
          <FormControl fullWidth error={showValidationErrors && !passwordValidated}>
            <PasswordTextField
              value={password}
              onChange={setPassword}
              label="New Password"
              hasError={showValidationErrors && !passwordValidated}
              helperText={
                resetFormSuccessfulOrNotSubmitted
                  ? passwordErrorMessage
                  : serverErrorMessages.password
              }
          />
          </FormControl>
          <FormControl fullWidth error={showValidationErrors && !confirmPasswordValidated}>
            <PasswordTextField
              value={confirmPassword}
              onChange={setConfirmPassword}
              label="Confirm Password"
              hasError={showValidationErrors && !confirmPasswordValidated}
              helperText={confirmPasswordErrorMessage}
            />
          </FormControl>
          <PrimaryButton
            fullWidth
            showActivity={isInProcess}
            onClick={resetPasswordWithForm}
          >
            Reset Password
          </PrimaryButton>
      </Stack>
      <ConfirmDialog
        title="Link Expired"
        subtext="This password reset link is no longer valid. Please request a new one."
        isOpen={!isValidLink}
        handleClose={() => window.location.replace(`${config.URL_MARKETPLACE}/account/login`)}
        proceed={() => window.location.replace(`${config.URL_MARKETPLACE}/account/forgot-password`)}
        proceedButtonText="Request New Link"
      />
      <ConfirmDialog
        title="Something Went Wrong"
        subtext="We couldn't reset your password due to a technical issue on our end. Please wait a moment and try again."
        isOpen={wasResetPasswordUnsuccessful || hasServerError}
        handleClose={() => window.location.reload()}
        proceed={() => window.location.reload()}
        proceedButtonText="Refresh Page"
      />
      <ConfirmDialog
          title="Password Reset Successful"
          subtext="Your password has been updated. You can now log in with your new password."
          isOpen={Boolean(resettingComplete)}
          handleClose={() => window.location.replace(`${config.URL_MARKETPLACE}/account/login`)}
          proceed={() => window.location.replace(`${config.URL_MARKETPLACE}/account/login`)}
          proceedButtonText="Log In"
      />
    </ResetPasswordCard>
  );
}
